import CleanButton from '../../../components/CleanButton/CleanButton';
import Form from '../../../components/Form/Form';
import PasswordField from '../../../components/PasswordField/PasswordField';
import Row from '../../../components/Row/Row';
import Spacer from '../../../components/Spacer/Spacer';
import Stack from '../../../components/Stack/Stack';
import SubmitButton from '../../../components/SubmitButton/SubmitButton';
import Text from '../../../components/Text/Text';
import TextField from '../../../components/TextField/TextField';
import Title from '../../../components/Title/Title';
import UncaughtErrors from '../../../components/UncaughtErrors/UncaughtErrors';
import useTrans from '../../../hooks/ui/useTrans';
import useBookingReferenceOrParameters from '../../hooks/useBookingReferenceOrParameters';
import useBookingFormProps from '../../pages/BookingForm/hooks/useBookingFormProps';

type LoginFormProps = {
    actionUrl: string;
    onForgotPassword?: () => void;
    layout?: 'stacked' | 'inline';
};

const LoginForm = ({
    actionUrl,
    onForgotPassword,
    layout = 'inline',
}: LoginFormProps) => {
    const { errors } = useBookingFormProps();
    const trans = useTrans('CustomerArea.Shared.LoginForm.');

    const extraParameters = useBookingReferenceOrParameters();

    const injectBookingDetails = (data: FormData) => {
        if (!extraParameters) {
            return data;
        }

        Object.entries(extraParameters).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            data.append(key, value);
        });

        return data;
    };

    const fields =
        layout === 'stacked' ? (
            <>
                <TextField
                    name="username"
                    errors={errors?.username}
                    label={trans('UsernameLabel')}
                    autoComplete="username"
                />

                <PasswordField
                    name="password"
                    errors={errors?.password}
                    autoComplete="current-password"
                    label={trans('PasswordLabel')}
                />

                {onForgotPassword && (
                    <Row horizontal="right">
                        <CleanButton onClick={onForgotPassword} tabIndex={-1}>
                            <Text lineHeight="small">
                                {trans('ForgotPasswordLink')}
                            </Text>
                        </CleanButton>
                    </Row>
                )}

                <Spacer vertical="none" />

                <Row horizontal="right">
                    <SubmitButton size="large">
                        {trans('SubmitButton')}
                    </SubmitButton>
                </Row>
            </>
        ) : (
            <Stack>
                <Row>
                    <TextField
                        name="username"
                        errors={errors?.username}
                        label={trans('UsernameLabel')}
                        autoComplete="username"
                        flex
                    />
                    <PasswordField
                        name="password"
                        errors={errors?.password}
                        autoComplete="current-password"
                        label={
                            <Row horizontal="justify">
                                <Text lineHeight="small">
                                    {trans('PasswordLabel')}
                                </Text>
                                {onForgotPassword && (
                                    <CleanButton
                                        onClick={onForgotPassword}
                                        tabIndex={-1}
                                    >
                                        <Text lineHeight="small">
                                            {trans('ForgotPasswordLink')}
                                        </Text>
                                    </CleanButton>
                                )}
                            </Row>
                        }
                        flex
                    />
                </Row>
                <Row horizontal="right">
                    <SubmitButton size="large">
                        {trans('SubmitButton')}
                    </SubmitButton>
                </Row>
            </Stack>
        );

    return (
        <Form
            method="post"
            action={actionUrl}
            modifyFormData={injectBookingDetails}
        >
            <Stack>
                <Title lineHeight="small">{trans('Title')}</Title>

                {fields}

                <UncaughtErrors
                    errors={errors}
                    exclude={['username', 'password']}
                />
            </Stack>
        </Form>
    );
};

export default LoginForm;
